import { useMutation, useQueryClient } from '@tanstack/react-query';

import { handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { useUiStore } from '@/store/ui';

const { get, post } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/`;

// Types

export type BulkAsignarParams = {
  ticket_ids: string[];
  usuario_asignado_id: number;
  usuario_asignado_nombre: string;
};

export type BulkEscalarParams = {
  ticket_ids: string[];
  area_destino: string;
  comentario: string;
};

export type BulkProgressError = {
  ticket_id: string;
  error: string;
};

export type BulkProgressState = {
  estado: 'PENDIENTE' | 'PROCESANDO' | 'COMPLETADO' | 'ERROR';
  progreso: number;
  total: number;
  porcentaje: number;
  descripcion: string;
  errores: BulkProgressError[];
};

export type BulkOperationResponse = {
  operacion_id: string;
  task_id: string;
  total: number;
};

// API functions

export const bulkAsignarTickets = async (data: BulkAsignarParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return post<{ code: number; status: string; data: BulkOperationResponse }>(
    `${BASE_URL}tickets/bulk/asignar/`, data, true
  );
};

export const bulkEscalarTickets = async (data: BulkEscalarParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return post<{ code: number; status: string; data: BulkOperationResponse }>(
    `${BASE_URL}tickets/bulk/escalar/`, data, true
  );
};

export const getBulkProgress = async (operacionId: string) => {
  return get<{ code: number; status: string; data: BulkProgressState }>(
    `${BASE_URL}tickets/bulk/progreso/${operacionId}/`, true
  );
};

// Hooks

// Helper: refetch inmediato de la lista, contadores (ContadoresEstado /
// useFetchTicketResumen), detalle, historial y archivos. Defensa por si
// el polling del progreso bulk no llega a disparar onComplete (modal cerrado
// temprano, error en el polling, etc.).
const refrescarTicketsActivos = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' });
};

export const useBulkAsignarTickets = () => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkAsignarTickets,
    onSuccess: () => { refrescarTicketsActivos(queryClient); },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const useBulkEscalarTickets = () => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkEscalarTickets,
    onSuccess: () => { refrescarTicketsActivos(queryClient); },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

// Reabrir masivo

export type BulkReabrirParams = {
  ticket_ids: string[];
};

export const bulkReabrirTickets = async (data: BulkReabrirParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return post<{ code: number; status: string; data: BulkOperationResponse }>(
    `${BASE_URL}tickets/bulk/reabrir/`, data, true
  );
};

export const useBulkReabrirTickets = () => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkReabrirTickets,
    onSuccess: () => { refrescarTicketsActivos(queryClient); },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};
